import { Injectable } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { Orders } from '../../interfaces/orders';

/**
 * OrdersSelectionService
 * Gestiona la selección múltiple de órdenes
 * Responsabilidad: Selection Management
 */
@Injectable({
  providedIn: 'root'
})
export class OrdersSelectionService {
  
  // Modelo de selección (permite múltiples selecciones)
  public selection = new SelectionModel<string>(true, []);

  // Set para almacenar referencias seleccionadas (persistencia entre páginas)
  private selectedReferencesSet = new Set<string>();

  constructor() {
  }

  /**
   * Verifica si una orden está seleccionada
   */
  isSelected(reference: string): boolean {
    return this.selection.isSelected(reference);
  }

  /**
   * Alterna la selección de una orden
   */
  toggle(reference: string): void {
    this.selection.toggle(reference);
    
    // Sincronizar con el set de referencias
    if (this.selection.isSelected(reference)) {
      this.selectedReferencesSet.add(reference);
    } else {
      this.selectedReferencesSet.delete(reference);
    }
  }

  /**
   * Selecciona todas las órdenes de la página actual
   */
  selectAll(orders: Orders[]): void {
    orders.forEach(order => {
      if (!this.selection.isSelected(order.reference)) {
        this.selection.select(order.reference);
        this.selectedReferencesSet.add(order.reference);
      }
    });
  }

  /**
   * Deselecciona todas las órdenes de la página actual
   */
  deselectAll(orders: Orders[]): void {
    orders.forEach(order => {
      this.selection.deselect(order.reference);
      this.selectedReferencesSet.delete(order.reference);
    });
  }

  /**
   * Limpia toda la selección
   */
  clearSelection(): void {
    this.selection.clear();
    this.selectedReferencesSet.clear();
  }

  /**
   * Obtiene el número de órdenes seleccionadas
   */
  getSelectedCount(): number {
    return this.selection.selected.length;
  }

  /**
   * Obtiene las referencias seleccionadas como array
   */
  getSelectedReferences(): string[] {
    return this.selection.selected;
  }

  /**
   * Obtiene el set de referencias seleccionadas (para persistencia)
   */
  getSelectedReferencesSet(): Set<string> {
    return this.selectedReferencesSet;
  }

  /**
   * Verifica si todas las órdenes visibles están seleccionadas
   */
  areAllSelected(orders: Orders[]): boolean {
    if (orders.length === 0) {
      return false;
    }
    return orders.every(order => this.selection.isSelected(order.reference));
  }

  /**
   * Verifica si algunas (pero no todas) las órdenes están seleccionadas
   */
  areSomeSelected(orders: Orders[]): boolean {
    if (orders.length === 0) {
      return false;
    }
    const selectedCount = orders.filter(order => 
      this.selection.isSelected(order.reference)
    ).length;
    return selectedCount > 0 && selectedCount < orders.length;
  }

  /**
   * Alterna la selección de todas las órdenes
   */
  toggleAll(orders: Orders[]): void {
    if (this.areAllSelected(orders)) {
      this.deselectAll(orders);
    } else {
      this.selectAll(orders);
    }
  }

  /**
   * Restaura selecciones desde el set (útil al cambiar de página)
   */
  restoreSelections(orders: Orders[]): void {
    orders.forEach(order => {
      if (this.selectedReferencesSet.has(order.reference)) {
        if (!this.selection.isSelected(order.reference)) {
          this.selection.select(order.reference);
        }
      }
    });
  }

  /**
   * Verifica si hay alguna orden seleccionada
   */
  hasSelection(): boolean {
    return this.selection.selected.length > 0;
  }
}
